import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AudioLayout from "../AudioLayout";
import { audioApi, track } from "../api";
import { useAudioAuth } from "../AudioAuthContext";
import { useAudioAccess } from "../AudioAccessContext";
import PlayPaywallModal from "../components/PlayPaywallModal";
import usePlayPaywall from "../usePlayPaywall";
import { ArrowLeft, Lock, PlayCircle, Sparkles } from "lucide-react";

export default function AudioBook() {
  const { bookId } = useParams();
  const nav = useNavigate();
  const { user, subscribed } = useAudioAuth();
  const access = useAudioAccess();
  const [book, setBook] = useState(null);
  const paywall = usePlayPaywall({ defaultNext: `/audio-comics/books/${bookId}` });

  useEffect(() => {
    audioApi.get(`/books/${bookId}`).then(r => setBook(r.data)).catch(() => setBook(false));
    if (user) track("book_opened", { book_id: bookId });
  }, [bookId, user]);

  if (book === null) return <AudioLayout><div className="text-center text-white/60 py-16">Loading…</div></AudioLayout>;
  if (book === false) return <AudioLayout><div className="text-center text-white/70 py-16">Book not found.</div></AudioLayout>;

  // FREE mode → everyone plays directly. PAID mode → only active subscribers.
  const canPlayDirectly = access.is_free || subscribed;

  const handlePlay = (vachana) => {
    if (canPlayDirectly) {
      nav(`/audio-comics/vachana/${vachana.vachana_id}`);
      return;
    }
    paywall.promptPaywall(`/audio-comics/vachana/${vachana.vachana_id}`);
  };

  const showLockedBadges = !canPlayDirectly;
  const showBookPaywall = !access.is_free && !subscribed;

  return (
    <AudioLayout>
      <Link to="/audio-comics/books" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-6" data-testid="ac-back-library"><ArrowLeft size={14}/> Library</Link>

      <div className="grid md:grid-cols-12 gap-8">
        <div className="md:col-span-4">
          <div className="aspect-[3/4] rounded-2xl overflow-hidden ring-1 ring-white/10 bg-white/5 sticky top-24">
            {book.thumbnail_url && <img src={book.thumbnail_url} alt="" className="w-full h-full object-cover"/>}
          </div>
        </div>
        <div className="md:col-span-8">
          <div className="text-[11px] uppercase tracking-[0.25em] text-amber-300 mb-2">Audio Book</div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight" data-testid="ac-book-title">{book.title}</h1>
          {book.description && <p className="mt-3 text-white/70 leading-relaxed">{book.description}</p>}

          {showBookPaywall && (
            <div className="mt-5 rounded-xl bg-fuchsia-500/10 border border-fuchsia-400/30 p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="ac-book-paywall">
              <div className="flex items-center gap-2 text-sm">
                <Lock size={14} className="text-fuchsia-300"/>
                <span>Preview the catalogue freely · subscribe to play</span>
              </div>
              <Link
                to="/audio-comics/subscription"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400 text-black font-semibold text-sm hover:bg-amber-300"
                data-testid="ac-book-subscribe"
              >
                <Sparkles size={14}/> {access.cta_secondary}
              </Link>
            </div>
          )}

          <h2 className="mt-9 mb-4 text-lg font-semibold">Vachanas <span className="text-white/40 text-sm">· {book.vachanas?.length || 0}</span></h2>
          <ul className="grid sm:grid-cols-2 gap-3">
            {(book.vachanas || []).map((v, i) => (
              <li key={v.vachana_id} data-testid={`ac-vachana-${v.vachana_id}`}>
                <button
                  type="button"
                  onClick={() => handlePlay(v)}
                  className="w-full text-left"
                  data-testid={`ac-vachana-play-${v.vachana_id}`}
                >
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/10 hover:border-amber-400/40 hover:bg-white/[0.06] transition">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-black/40 shrink-0 relative">
                      {v.thumbnail_url && <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover"/>}
                      <div className="absolute inset-0 grid place-items-center bg-black/30">
                        <PlayCircle size={28} className="text-white drop-shadow"/>
                      </div>
                      {showLockedBadges && (
                        <div className="absolute top-1 right-1 grid place-items-center w-5 h-5 rounded-full bg-black/70 backdrop-blur">
                          <Lock size={10} className="text-white/85"/>
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-amber-300/80">Vachana {i + 1}</div>
                      <div className="font-medium text-sm leading-tight line-clamp-1">{v.title}</div>
                      <div className="text-xs text-white/50 line-clamp-1">{v.description}</div>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <PlayPaywallModal
        open={paywall.open}
        onClose={paywall.close}
        onSignIn={paywall.goSignIn}
        onSubscribe={paywall.goSubscribe}
      />
    </AudioLayout>
  );
}
